const STORAGE_KEY = "ddp-v2-ai-usage";
const DEFAULT_REGISTERED_DAILY_LIMIT = 10;

export const V2_AI_USER_TYPES = Object.freeze({
  guest: "guest",
  registered: "registered",
});

export const AI_LIMIT_REACHED_MESSAGE =
  "AI answers are limited for today. You can still use food search, dashboard summaries, history, and knowledge tools.";

export function getAiLimitReachedMessage() {
  return AI_LIMIT_REACHED_MESSAGE;
}

export function getAiUsageDateKey(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getServerAiDailyLimit({ userType = V2_AI_USER_TYPES.guest, appSettings = {} } = {}) {
  if (userType === V2_AI_USER_TYPES.guest && !appSettings.guest_server_ai_enabled) {
    return 0;
  }

  const configuredLimit = Number(appSettings.server_ai_daily_limit);
  if (Number.isFinite(configuredLimit) && configuredLimit >= 0) {
    return Math.floor(configuredLimit);
  }

  return userType === V2_AI_USER_TYPES.registered ? DEFAULT_REGISTERED_DAILY_LIMIT : 0;
}

export function canUseServerAi({ userType = V2_AI_USER_TYPES.guest, appSettings = {}, date = new Date() } = {}) {
  const limit = getServerAiDailyLimit({ userType, appSettings });
  const usage = loadAiUsage(date);
  const used = Number(usage.server_ai_calls) || 0;

  return {
    allowed: limit > 0 && used < limit,
    used,
    limit,
    remaining: Math.max(limit - used, 0),
  };
}

export function countServerAiCall({ userType = V2_AI_USER_TYPES.guest, appSettings = {}, date = new Date() } = {}) {
  const decision = canUseServerAi({ userType, appSettings, date });
  if (!decision.allowed) {
    return { ok: false, ...decision };
  }

  const usage = loadAiUsage(date);
  const nextUsage = {
    ...usage,
    server_ai_calls: (Number(usage.server_ai_calls) || 0) + 1,
  };
  saveAiUsage(nextUsage);

  return {
    ok: true,
    ...canUseServerAi({ userType, appSettings, date }),
  };
}

export function localAssistantAnswerUsageResult() {
  return {
    counted: false,
    reason: "Local read-only answers are not counted.",
  };
}

export function loadAiUsage(date = new Date()) {
  if (typeof window === "undefined") {
    return { date: getAiUsageDateKey(date), server_ai_calls: 0 };
  }

  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    const dateKey = getAiUsageDateKey(date);
    if (saved.date !== dateKey) {
      return { date: dateKey, server_ai_calls: 0 };
    }
    return {
      date: dateKey,
      server_ai_calls: Number(saved.server_ai_calls) || 0,
    };
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return { date: getAiUsageDateKey(date), server_ai_calls: 0 };
  }
}

export function saveAiUsage(usage) {
  const safeUsage = {
    date: usage.date || getAiUsageDateKey(),
    server_ai_calls: Number(usage.server_ai_calls) || 0,
  };

  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safeUsage));
  }

  return safeUsage;
}
